import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useSalon } from "../context/SalonContext";
import { formatDate } from "../lib/format";
import api from "../lib/api";
import AdminTenantDetail from "./AdminTenantDetail";
import { PlanBadge, StatusBadge, useConfirmModal } from "./adminShared";

/**
 * AdminClients — Lista de Clientes Registrados na plataforma.
 * Primeiro módulo do painel mestre (super-admin).
 */
const AdminClients = () => {
    const { registrations } = useSalon();
    const [query, setQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [remote, setRemote] = useState(null); // tenants do backend (com utm_*)
    const [utmSummary, setUtmSummary] = useState([]);
    const [deletedTenants, setDeletedTenants] = useState([]);
    const [deletedOpen, setDeletedOpen] = useState(false);
    const [detailTenantId, setDetailTenantId] = useState(null); // abre a tela cheia de detalhes
    const { requestConfirm, ConfirmModalElement } = useConfirmModal();

    // Merge local (localStorage) + remote (backend)
    const combined = useMemo(() => {
        if (!Array.isArray(remote)) return registrations;
        const norm = remote.map((t) => ({
            id: t.id,
            shopName: t.shop_name,
            shopId: t.shop_id,
            ownerName: t.owner_name,
            email: t.email,
            phone: t.phone,
            document: t.document || "—",
            kind: t.kind || "PF",
            plan: (t.plan || "").charAt(0).toUpperCase() + (t.plan || "").slice(1),
            status: t.status,
            mrr: t.mrr || 0,
            createdAt: t.created_at ? new Date(t.created_at).getTime() : Date.now(),
            trial_ends_at: t.trial_ends_at,
            // UTM fields
            utm_source: t.utm_source,
            utm_medium: t.utm_medium,
            utm_campaign: t.utm_campaign,
            utm_content: t.utm_content,
            utm_term: t.utm_term,
            referrer: t.referrer,
            signup_source: t.signup_source,
            business_type: t.business_type,
            city: t.city,
            state: t.state,
        }));
        // Se o backend responde, ele é a fonte da verdade
        return norm;
    }, [remote, registrations]);

    const loadTenants = useCallback(async () => {
        try {
            const [tenants, utm, deleted] = await Promise.all([
                api.get("/admin/tenants").catch(() => null),
                api.get("/admin/utm/summary").catch(() => null),
                api.get("/admin/tenants/deleted").catch(() => null),
            ]);
            if (Array.isArray(tenants)) setRemote(tenants);
            if (Array.isArray(utm)) setUtmSummary(utm);
            if (Array.isArray(deleted)) setDeletedTenants(deleted);
        } catch (_) { /* silent */ }
    }, []);

    useEffect(() => {
        loadTenants();
    }, [loadTenants]);

    const filtered = useMemo(() => {
        return combined.filter((r) => {
            const q = query.trim().toLowerCase();
            const match =
                !q ||
                (r.shopName || "").toLowerCase().includes(q) ||
                (r.ownerName || "").toLowerCase().includes(q) ||
                (r.email || "").toLowerCase().includes(q) ||
                (r.shopId || "").toLowerCase().includes(q) ||
                (r.document || "").includes(q) ||
                (r.utm_source || "").toLowerCase().includes(q) ||
                (r.utm_campaign || "").toLowerCase().includes(q);
            const statusOk = statusFilter === "all" || r.status === statusFilter;
            return match && statusOk;
        });
    }, [combined, query, statusFilter]);

    const kpis = useMemo(() => {
        const total = combined.length;
        const active = combined.filter((r) => r.status === "active").length;
        const trial = combined.filter((r) => r.status === "trial").length;
        const churn = combined.filter((r) => r.status === "cancelled").length;
        const mrr = combined.reduce((s, r) => s + (r.mrr || 0), 0);
        return { total, active, trial, churn, mrr };
    }, [combined]);

    const handleDelete = (r) => {
        requestConfirm({
            message: `Tem certeza que deseja desativar ${r.shopName}? Esta ação pode ser revertida em Tenants Desativados.`,
            confirmLabel: "Desativar",
            danger: true,
            onConfirm: async () => {
                try {
                    await api.del(`/admin/tenants/${r.id}`);
                    toast.success("Tenant desativado", { description: `${r.shopName} movido para Tenants Desativados.` });
                    await loadTenants();
                } catch (e) {
                    toast.error("Falha ao desativar", { description: e?.message || "Tente novamente." });
                }
            },
        });
    };

    const handleRestore = async (t) => {
        try {
            await api.patch(`/admin/tenants/${t.id}/restore`);
            toast.success("Tenant reativado", { description: `${t.shop_name} está ativo novamente.` });
            await loadTenants();
        } catch (e) {
            toast.error("Falha ao reabilitar", { description: e?.message || "Tente novamente." });
        }
    };

    const handleToggleStatus = async (r) => {
        const next = r.status === "active" ? "suspended" : "active";
        try {
            await api.patch(`/admin/tenants/${r.id}/${next === "active" ? "activate" : "suspend"}`);
            toast.success(`Status atualizado → ${next}`);
            await loadTenants();
        } catch (e) {
            toast.error("Falha ao atualizar status", { description: e?.message || "Tente novamente." });
        }
    };

    if (detailTenantId) {
        return (
            <AdminTenantDetail
                tenantId={detailTenantId}
                tenant={(remote || []).find((t) => t.id === detailTenantId) || null}
                onBack={() => setDetailTenantId(null)}
                onTenantUpdated={loadTenants}
            />
        );
    }

    return (
        <div data-testid="admin-clients-view" className="space-y-6">
            <header className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-indigo-400">
                        Módulo 01 · Plataforma
                    </div>
                    <h1 className="salon-heading text-3xl text-white mt-1">Clientes Registrados</h1>
                    <p className="text-sm text-slate-400 mt-1">
                        Todos os salões e barbearias cadastrados na plataforma Kinng.app.
                    </p>
                </div>
                <button
                    data-testid="admin-export-btn"
                    onClick={() => toast.info("Exportação", { description: "CSV gerado em /reports/clients.csv (demo)." })}
                    className="min-h-[44px] px-4 rounded-xl border border-white/10 hover:border-indigo-500/60 text-slate-300 hover:text-white text-xs uppercase tracking-wider transition-all"
                >
                    <i className="fa-solid fa-file-export mr-2"></i>Exportar CSV
                </button>
            </header>

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <AdminKpi label="Total" value={kpis.total} icon="fa-users" />
                <AdminKpi label="Ativos" value={kpis.active} icon="fa-circle-check" color="#10b981" />
                <AdminKpi label="Em trial" value={kpis.trial} icon="fa-clock" color="#f59e0b" />
                <AdminKpi label="Cancelados" value={kpis.churn} icon="fa-circle-xmark" color="#ef4444" />
                <AdminKpi label="MRR total" value={`R$ ${kpis.mrr.toFixed(0)}`} icon="fa-sack-dollar" color="#6366f1" />
            </div>

            {/* UTM breakdown — origens de aquisição */}
            {utmSummary.length > 0 && (
                <div className="rounded-2xl border border-white/5 bg-gradient-to-br from-indigo-500/5 to-violet-500/5 p-5" data-testid="admin-utm-summary">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.3em] text-indigo-300">Aquisição</div>
                            <h2 className="salon-heading text-lg text-white mt-0.5">Origem dos cadastros (self-signup)</h2>
                        </div>
                        <span className="text-[10px] text-slate-500 uppercase tracking-widest">UTM · Referrer</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
                        {utmSummary.slice(0, 10).map((row) => (
                            <button
                                key={row.source}
                                data-testid={`admin-utm-${row.source}`}
                                onClick={() => setQuery(row.source === "direct" ? "" : row.source)}
                                className="text-left rounded-xl border border-white/10 bg-slate-950/40 hover:border-indigo-500/40 p-3 transition-all"
                            >
                                <div className="flex items-center gap-1.5 mb-1">
                                    <UtmIcon source={row.source} />
                                    <div className="text-xs text-slate-300 font-medium truncate">{row.source}</div>
                                </div>
                                <div className="flex items-baseline gap-2">
                                    <div className="text-2xl font-mono text-white">{row.tenants}</div>
                                    <div className="text-[10px] text-slate-500">tenant{row.tenants === 1 ? "" : "s"}</div>
                                </div>
                                <div className="text-[10px] text-slate-500 mt-0.5 flex gap-2">
                                    <span className="text-emerald-300">{row.active} ativo{row.active === 1 ? "" : "s"}</span>
                                    <span className="text-amber-300">{row.trial} trial</span>
                                </div>
                                {row.mrr > 0 && <div className="text-[10px] font-mono text-indigo-300 mt-1">R$ {row.mrr.toFixed(0)} MRR</div>}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Filters */}
            <div className="rounded-2xl border border-white/5 bg-slate-900/40 backdrop-blur-xl p-4 flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-[240px] flex items-center gap-2 rounded-xl border border-white/10 bg-slate-950/60 px-3 py-2 focus-within:border-indigo-500/60 transition-colors">
                    <i className="fa-solid fa-magnifying-glass text-slate-400 text-sm"></i>
                    <input
                        data-testid="admin-search-input"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Buscar por nome, e-mail, CPF/CNPJ, identificador…"
                        className="flex-1 bg-transparent text-sm text-white outline-none min-h-[36px] placeholder:text-slate-500"
                    />
                </div>
                <div className="flex items-center gap-1 bg-slate-950/60 rounded-xl p-1 border border-white/5">
                    {[
                        { id: "all", label: "Todos" },
                        { id: "active", label: "Ativos" },
                        { id: "trial", label: "Trial" },
                        { id: "suspended", label: "Suspensos" },
                    ].map((f) => (
                        <button
                            key={f.id}
                            data-testid={`admin-filter-${f.id}`}
                            onClick={() => setStatusFilter(f.id)}
                            className={`min-h-[40px] px-3 rounded-lg text-xs font-semibold uppercase tracking-wider transition-all ${
                                statusFilter === f.id
                                    ? "bg-indigo-500 text-white"
                                    : "text-slate-400 hover:text-white"
                            }`}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Table */}
            <div
                className="rounded-2xl border border-white/5 bg-slate-900/40 backdrop-blur-xl overflow-hidden"
                data-testid="admin-clients-table"
            >
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-[10px] uppercase tracking-[0.25em] text-slate-500 bg-slate-950/60">
                                <th className="text-left py-3 px-4">Salão / Dono</th>
                                <th className="text-left py-3 px-4">Documento</th>
                                <th className="text-left py-3 px-4">Contato</th>
                                <th className="text-left py-3 px-4">Plano</th>
                                <th className="text-left py-3 px-4">Origem</th>
                                <th className="text-left py-3 px-4">Cadastro</th>
                                <th className="text-right py-3 px-4">MRR</th>
                                <th className="text-center py-3 px-4">Status</th>
                                <th className="text-right py-3 px-4">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 && (
                                <tr>
                                    <td colSpan={9} className="py-16 text-center text-slate-500">
                                        <i className="fa-solid fa-users-slash text-3xl mb-3 opacity-40"></i>
                                        <p>Nenhum cliente encontrado</p>
                                    </td>
                                </tr>
                            )}
                            {filtered.map((r) => (
                                <tr
                                    key={r.id}
                                    data-testid={`admin-row-${r.id}`}
                                    onClick={() => setDetailTenantId(r.id)}
                                    className="border-t border-white/5 hover:bg-white/5 transition-colors cursor-pointer"
                                >
                                    <td className="py-3 px-4">
                                        <div className="flex items-center gap-3">
                                            <div
                                                className="h-10 w-10 rounded-xl flex items-center justify-center text-white font-bold text-xs"
                                                style={{
                                                    background: `hsl(${(r.shopName.charCodeAt(0) * 17) % 360}, 60%, 45%)`,
                                                }}
                                            >
                                                {r.shopName.slice(0, 2).toUpperCase()}
                                            </div>
                                            <div className="min-w-0">
                                                <div className="text-white font-semibold truncate">{r.shopName}</div>
                                                <div className="text-[11px] text-slate-400 truncate">
                                                    {r.ownerName} · <span className="font-mono">/{r.shopId}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="py-3 px-4">
                                        <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold text-slate-400">
                                            <span className="px-1.5 py-0.5 rounded bg-white/5 text-slate-300">
                                                {r.kind}
                                            </span>
                                        </span>
                                        <div className="text-[11px] font-mono text-slate-300 mt-0.5">
                                            {r.document}
                                        </div>
                                    </td>
                                    <td className="py-3 px-4">
                                        <div className="text-[12px] text-slate-300 truncate max-w-[180px]">{r.email}</div>
                                        <div className="text-[11px] text-slate-500 font-mono">{r.phone}</div>
                                    </td>
                                    <td className="py-3 px-4">
                                        <PlanBadge plan={r.plan} />
                                    </td>
                                    <td className="py-3 px-4">
                                        <UtmCell row={r} />
                                    </td>
                                    <td className="py-3 px-4 text-xs text-slate-400">{formatDate(r.createdAt)}</td>
                                    <td className="py-3 px-4 text-right font-mono text-white">
                                        {r.mrr ? `R$ ${r.mrr.toFixed(0)}` : <span className="text-slate-600">—</span>}
                                    </td>
                                    <td className="py-3 px-4 text-center">
                                        <StatusBadge status={r.status} />
                                    </td>
                                    <td className="py-3 px-4 text-right">
                                        <div className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                            <button
                                                data-testid={`admin-view-${r.id}`}
                                                onClick={() => setDetailTenantId(r.id)}
                                                title="Ver detalhes"
                                                className="h-9 w-9 rounded-lg bg-white/5 hover:bg-indigo-500/20 hover:text-indigo-400 text-slate-400 transition-all"
                                            >
                                                <i className="fa-solid fa-eye text-xs"></i>
                                            </button>
                                            <button
                                                data-testid={`admin-toggle-${r.id}`}
                                                onClick={() => handleToggleStatus(r)}
                                                title="Alternar status"
                                                className="h-9 w-9 rounded-lg bg-white/5 hover:bg-amber-500/20 hover:text-amber-400 text-slate-400 transition-all"
                                            >
                                                <i className={`fa-solid ${r.status === "active" ? "fa-pause" : "fa-play"} text-xs`}></i>
                                            </button>
                                            <button
                                                data-testid={`admin-delete-${r.id}`}
                                                onClick={() => handleDelete(r)}
                                                title="Desativar"
                                                className="h-9 w-9 rounded-lg bg-white/5 hover:bg-red-500/20 hover:text-red-400 text-slate-400 transition-all"
                                            >
                                                <i className="fa-solid fa-trash text-xs"></i>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="px-4 py-3 border-t border-white/5 text-[11px] text-slate-500 flex items-center justify-between">
                    <span>
                        Exibindo <span className="text-white font-mono">{filtered.length}</span> de{" "}
                        <span className="text-white font-mono">{registrations.length}</span> registros
                    </span>
                    <span>
                        <i className="fa-solid fa-database mr-1.5"></i>localStorage · `gereBeauty.registrations.v1`
                    </span>
                </div>
            </div>

            {/* Tenants Desativados — soft-deleted, reversível via Reativar. Colapsável. */}
            <div className="rounded-2xl border border-white/5 bg-slate-900/40 backdrop-blur-xl overflow-hidden" data-testid="admin-deleted-tenants">
                <button
                    type="button"
                    data-testid="admin-deleted-toggle"
                    onClick={() => setDeletedOpen((v) => !v)}
                    className="w-full px-4 py-3 border-b border-white/5 flex items-center justify-between text-left min-h-[44px]"
                >
                    <div className="flex items-center gap-2">
                        <i className="fa-solid fa-trash-can text-slate-500 text-sm"></i>
                        <h2 className="text-sm font-semibold text-slate-300">Tenants Desativados</h2>
                        <span className="text-[10px] uppercase tracking-wider text-slate-600 bg-white/5 px-2 py-0.5 rounded-full">
                            {deletedTenants.length}
                        </span>
                    </div>
                    <i className={`fa-solid fa-chevron-down text-slate-500 text-xs transition-transform ${deletedOpen ? "rotate-180" : ""}`}></i>
                </button>
                {deletedOpen && (
                    deletedTenants.length === 0 ? (
                        <div className="py-8 text-center text-slate-600 text-xs">Nenhum tenant desativado no momento.</div>
                    ) : (
                        <div className="divide-y divide-white/5">
                            {deletedTenants.map((t) => (
                                <div key={t.id} data-testid={`admin-deleted-row-${t.id}`} className="flex items-center justify-between gap-3 px-4 py-3">
                                    <div className="min-w-0">
                                        <div className="text-white text-sm font-semibold truncate">{t.shop_name}</div>
                                        <div className="text-[11px] text-slate-500 truncate">
                                            {t.email} · desativado em {t.deleted_at ? formatDate(new Date(t.deleted_at).getTime()) : "—"}
                                        </div>
                                    </div>
                                    <button
                                        data-testid={`admin-restore-${t.id}`}
                                        onClick={() => handleRestore(t)}
                                        className="shrink-0 min-h-[36px] px-3 rounded-lg border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/15 text-xs uppercase tracking-wider font-semibold transition-all"
                                    >
                                        <i className="fa-solid fa-rotate-left mr-1.5"></i>Reativar
                                    </button>
                                </div>
                            ))}
                        </div>
                    )
                )}
            </div>

            {ConfirmModalElement}
        </div>
    );
};

const AdminKpi = ({ label, value, icon, color = "#6366f1" }) => (
    <div className="rounded-2xl border border-white/5 bg-slate-900/40 backdrop-blur-xl p-4">
        <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">{label}</div>
            <div
                className="h-8 w-8 rounded-lg flex items-center justify-center text-sm"
                style={{ background: `${color}22`, color }}
            >
                <i className={`fa-solid ${icon}`}></i>
            </div>
        </div>
        <div className="salon-heading text-2xl text-white mt-2">{value}</div>
    </div>
);

// ─── UTM helpers ────────────────────────────────────────────────────────
const UTM_STYLE = {
    instagram: { icon: "fa-instagram", brand: true, color: "#E1306C", label: "Instagram" },
    facebook:  { icon: "fa-facebook",  brand: true, color: "#1877F2", label: "Facebook" },
    google:    { icon: "fa-google",    brand: true, color: "#EA4335", label: "Google" },
    youtube:   { icon: "fa-youtube",   brand: true, color: "#FF0000", label: "YouTube" },
    tiktok:    { icon: "fa-tiktok",    brand: true, color: "#EE1D52", label: "TikTok" },
    twitter:   { icon: "fa-x-twitter", brand: true, color: "#e2e8f0", label: "X / Twitter" },
    linkedin:  { icon: "fa-linkedin",  brand: true, color: "#0A66C2", label: "LinkedIn" },
    whatsapp:  { icon: "fa-whatsapp",  brand: true, color: "#25D366", label: "WhatsApp" },
    email:     { icon: "fa-envelope",  brand: false, color: "#94a3b8", label: "E-mail" },
    referral:  { icon: "fa-user-group",brand: false, color: "#a78bfa", label: "Indicação" },
    direct:    { icon: "fa-globe",     brand: false, color: "#64748b", label: "Direto" },
};
const utmMeta = (source) => UTM_STYLE[(source || "direct").toLowerCase()] || {
    icon: "fa-tag", brand: false, color: "#94a3b8", label: (source || "direct"),
};

const UtmIcon = ({ source }) => {
    const m = utmMeta(source);
    return (
        <span
            className="h-5 w-5 rounded-md flex items-center justify-center shrink-0"
            style={{ background: `${m.color}22`, color: m.color }}
        >
            <i className={`${m.brand ? "fa-brands" : "fa-solid"} ${m.icon} text-[10px]`}></i>
        </span>
    );
};

const UtmCell = ({ row }) => {
    const source = row.utm_source;
    if (!source && row.signup_source !== "self_signup") {
        return <span className="text-[10px] uppercase tracking-widest text-slate-600">manual</span>;
    }
    const m = utmMeta(source || "direct");
    return (
        <div className="flex items-center gap-2 min-w-0" title={row.utm_campaign ? `Campanha: ${row.utm_campaign}` : m.label}>
            <UtmIcon source={source} />
            <div className="min-w-0">
                <div className="text-xs text-slate-200 truncate">{m.label}</div>
                {row.utm_campaign && (
                    <div className="text-[10px] text-slate-500 truncate font-mono">{row.utm_campaign}</div>
                )}
            </div>
        </div>
    );
};

export default AdminClients;
